import bezierEasing from 'bezier-easing'
import limitNumberInRange from './limitNumberInRange'

type CubicTuple = [number, number, number, number]

const CUBIC_BEZIER = {
  linear: [0.0, 0.0, 1.0, 1.0],
  ease: [0.25, 0.1, 0.25, 1.0],
  'ease-in': [0.42, 0.0, 1.0, 1.0],
  'ease-in-out': [0.42, 0.0, 0.58, 1.0],
  'ease-out': [0.0, 0.0, 0.58, 1.0]
}

const isServer = typeof window === 'undefined'

const scrollState = new WeakMap()

export function smoothScrollTo (config) {
  if (isServer) {
    throw new Error('Can not scroll at server side.')
  }
  if (!config) {
    throw new Error('Invalid parameters.')
  }

  const { x, y, container = window, duration = 500, timingFunction = 'ease' } = config

  let cubicBezier: CubicTuple = null
  if (typeof timingFunction === 'string' && CUBIC_BEZIER[timingFunction]) {
    cubicBezier = CUBIC_BEZIER[timingFunction]
  } else if (Array.isArray(timingFunction) && timingFunction.length === 4) {
    cubicBezier = (timingFunction as CubicTuple)
  } else {
    throw new Error('Invalid timingFunction.')
  }

  const state = scrollState.get(container)
  if (state) {
    return new Promise((resolve) => {
      if (state.pendingTask) {
        state.pendingTask.abort()
      }
      state.pendingTask = function () {
        delete state.pendingTask
        smoothScrollTo(config).then(resolve)
      }
      state.pendingTask.abort = () => resolve(false)
    })
  }

  return new Promise((resolve) => {
    const startX = container === window ? window.pageXOffset : container.scrollLeft
    const startY = container === window ? window.pageYOffset : container.scrollTop
    const endX = (x === undefined) ? startX : Math.round(x)
    const endY = (y === undefined) ? startY : Math.round(y)
    const scrollDistanceX = endX - startX
    const scrollDistanceY = endY - startY

    if (!scrollDistanceX && !scrollDistanceY) {
      resolve(true)
      return
    }

    const doScroll = (left, top) => {
      if (container === window) {
        window.scrollTo(left, top)
      } else {
        container.scrollTo({ left, top })
      }
    }

    if (!duration) {
      doScroll(endX, endY)
      resolve(true)
      return
    }

    const start = performance.now()

    const progressFunction = bezierEasing(...cubicBezier)

    const scroll = (now) => {
      // If there's a new task coming during the scrolling, cancel the current one
      // immediately to launch the new one.
      const state = scrollState.get(container)
      if (state.pendingTask) {
        requestAnimationFrame(state.pendingTask)
        scrollState.delete(container)
        resolve(false)
        return
      }

      const progress = Math.min((now - start) / duration, 1)
      const ratio = progressFunction(progress)
      doScroll(startX + scrollDistanceX * ratio, startY + scrollDistanceY * ratio)

      if (ratio === 1) {
        scrollState.delete(container)
        resolve(true)
      } else {
        requestAnimationFrame(scroll)
      }
    }

    scrollState.set(container, {})
    requestAnimationFrame(scroll)
  })
}

export function smoothScrollToTop (duration) {
  return smoothScrollTo({ y: 0, duration })
}

export function smoothScrollToElement (elem, { duration = undefined, offset = 0 } = {}) {
  if (!elem) {
    throw new Error('Invalid parameters.')
  }
  const titlebar = document.getElementById('titlebar')
  if (titlebar) {
    offset -= titlebar.offsetHeight + parseInt(getComputedStyle(titlebar).getPropertyValue('top'))
  }
  const y = Math.ceil(elem.getBoundingClientRect().top) + window.pageYOffset + offset
  return smoothScrollTo({ y, duration })
}

export function smoothScrollElementToCenter ({ container, targetElement, duration = 400 }) {
  const distanceToLeftEdge = (container.offsetWidth - targetElement.offsetWidth) / 2
  const destinationX = targetElement.offsetLeft - distanceToLeftEdge
  return smoothScrollTo({
    container,
    duration,
    x: limitNumberInRange(destinationX, { min: 0, max: container.scrollWidth - container.offsetWidth })
  })
}
